// Language Changer's form (live's `language-changer-form` library view, spec G14-templates): the
// clip, one to five target languages, the Lip Sync Mode and the subtitles; one tile a language.
import { Divider, Group, Popover, Stack, Switch, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Check, Film, Info, Languages, Speech } from "lucide-react";
import { useState } from "react";

import {
  useGetCatalog,
  useListTemplateCaptionStyles,
} from "../../../api/generated/endpoints/generation/generation";
import type { Catalog, DubbingCaps, LipsyncCaps } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { sourceProblem } from "../../../platform/media/video-source";
import { GlassPanel } from "../../../ui/GlassPanel";
import { CostChip } from "../TemplateCost";
import {
  ReuseSwitch,
  StartArrow,
  VideoSlot,
  videoSource,
  type FormExample,
  type SlotVideo,
  type TemplateFormProps,
} from "./kit";
import classes from "./forms.module.css";
import { Subtitles, type SubtitlesValue } from "./Subtitles";

/** Live's models (`constants.ts`), by our catalog ids, and live's words when one is gone. */
const DUB_MODEL = "elevenlabs-video-dubbing";
const LIPSYNC_MODEL = "sync-lipsync-v3";
const DUB_MODEL_MISSING = "No video-dubbing model is available for this team";
const LIPSYNC_MODEL_MISSING = "No lip-sync model is available for this team.";
/** Live's words (`constants.ts`). */
const TITLE = "Language Changer";
const MAX_LANGUAGES = 5;
const VIDEO_TIP = "The source video you want to translate. Select from your library or upload a new one.";
const LANGUAGES_TIP =
  "Pick the languages to translate into. Choose at least 1 and up to 5 from the full supported list.";
const LIP_SYNC_TIP =
  "Automatically syncs the actor's lips to the new language's audio for a natural result. On by default.";
const SYNC_MODE_TIP =
  "Controls how closely the dubbed audio timing matches the original speech — pick the mode that best fits your video.";
const SUBTITLES_TIP =
  "Adds subtitles in the target language. On by default — turn off if you only need dubbed audio.";
const HANDOFF = "Your videos are finishing in My Generations — you can close this panel now.";

/** A model's caps for one of its modes, where the catalog lists the model. */
function dubbingOf(catalog: Catalog): DubbingCaps | null {
  return catalog.video_models.find((each) => each.id === DUB_MODEL)?.modes["video.dub"]?.dubbing ?? null;
}

function lipsyncOf(catalog: Catalog): LipsyncCaps | null {
  return catalog.video_models.find((each) => each.id === LIPSYNC_MODEL)?.modes["video.lipsync"]?.lipsync ?? null;
}

/** Live's trigger: "Languages", then "Languages (n)" (never the names, so its width stays). */
function languagesLabel(picked: readonly string[]): string {
  return picked.length === 0 ? "Languages" : `Languages (${picked.length})`;
}

/**
 * Live's languages popover (capture 20): "Target languages" and its "n / 5", then the dubbing
 * model's targets in its order, several picked in pick order, the rest locked at five.
 */
function LanguagesPicker(props: {
  options: DubbingCaps["target_languages"];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const { options, value, onChange } = props;
  const [opened, setOpened] = useState(false);
  const full = value.length >= MAX_LANGUAGES;
  const label = languagesLabel(value);
  return (
    <Popover opened={opened} onChange={setOpened} position="top-start" width={220} withinPortal={false}>
      <Popover.Target>
        <Tooltip label={LANGUAGES_TIP} multiline maw={280} withArrow disabled={opened}>
          <UnstyledButton
            className={classes.chip}
            aria-label={`Target languages: ${label}`}
            onClick={() => setOpened((open) => !open)}
          >
            <Languages size={16} strokeWidth={1.5} aria-hidden />
            <span>{label}</span>
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={4}>
        <Group justify="space-between" px={8} py={6}>
          <Text size="xs" c="dimmed">
            Target languages
          </Text>
          <Text size="xs" c="dimmed" aria-label="Picked languages">
            {value.length} / {MAX_LANGUAGES}
          </Text>
        </Group>
        <Divider mb={4} />
        <Stack gap={2}>
          {options.map((language) => {
            const ticked = value.includes(language.code);
            return (
              <UnstyledButton
                key={language.code}
                className={classes.option}
                aria-pressed={ticked}
                disabled={!ticked && full}
                onClick={() =>
                  onChange(ticked ? value.filter((each) => each !== language.code) : [...value, language.code])
                }
              >
                <span>{language.name}</span>
                {ticked ? <Check size={16} aria-hidden /> : null}
              </UnstyledButton>
            );
          })}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

interface LipSyncValue {
  on: boolean;
  /** The picked sync mode; null is the model's default (live's ''). */
  mode: string | null;
}

/**
 * Live's lip-sync popover (captures 21, 23, 24): "Lip Sync Mode", and while on "Sync mode" with
 * the lip-sync model's modes. The trigger reads the mode, or "Off"; with no lip-sync model it is
 * "Off" and disabled, in live's words.
 */
function LipSyncPicker(props: {
  caps: LipsyncCaps | null;
  value: LipSyncValue;
  onChange: (value: LipSyncValue) => void;
}) {
  const { caps, value, onChange } = props;
  const [opened, setOpened] = useState(false);
  const mode = value.mode ?? caps?.default_sync_mode ?? null;
  const modeLabel = caps?.sync_modes.find((each) => each.value === mode)?.label ?? null;
  const label = caps !== null && value.on && modeLabel !== null ? modeLabel : "Off";
  return (
    <Popover opened={opened} onChange={setOpened} position="top-start" width={220} withinPortal={false}>
      <Popover.Target>
        <Tooltip
          label={caps === null ? LIPSYNC_MODEL_MISSING : LIP_SYNC_TIP}
          multiline
          maw={280}
          withArrow
          disabled={opened}
        >
          <UnstyledButton
            className={classes.chip}
            aria-label={`Lip sync: ${label}`}
            disabled={caps === null}
            onClick={() => setOpened((open) => !open)}
          >
            <Speech size={16} strokeWidth={1.5} aria-hidden />
            <span>{label}</span>
          </UnstyledButton>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={12}>
        <Stack gap={8}>
          <Switch
            label="Lip Sync Mode"
            labelPosition="left"
            checked={value.on}
            onChange={(event) => onChange({ ...value, on: event.currentTarget.checked })}
          />
          {value.on && caps !== null ? (
            <>
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  Sync mode
                </Text>
                <Tooltip label={SYNC_MODE_TIP} multiline maw={260} withArrow>
                  <Info size={12} aria-label="About sync mode" />
                </Tooltip>
              </Group>
              <Stack gap={2}>
                {caps.sync_modes.map((option) => (
                  <UnstyledButton
                    key={option.value}
                    className={classes.option}
                    aria-pressed={option.value === mode}
                    onClick={() => onChange({ ...value, mode: option.value })}
                  >
                    <span>{option.label}</span>
                    {option.value === mode ? <Check size={16} aria-hidden /> : null}
                  </UnstyledButton>
                ))}
              </Stack>
            </>
          ) : null}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

interface Values {
  video: SlotVideo | null;
  languages: string[];
  lipSync: LipSyncValue;
  subtitles: SubtitlesValue;
}

const FRESH: Omit<Values, "video"> = {
  languages: [],
  lipSync: { on: true, mode: null },
  // The style is the server's default until the styles are read (live's '').
  subtitles: { on: true, position: "bottom", styleId: "" },
};

/** The form's values from the card's example (live's `build-example-values`), or none. */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "language-changer" || inputs.video.kind !== "example") return null;
  const key = inputs.video.key;
  return {
    video: { exampleKey: key, url: example.urls[key] ?? null, name: "Example", durationMs: null },
    languages: [...inputs.languages],
    lipSync: { on: inputs.lip_sync ?? true, mode: inputs.sync_mode ?? null },
    subtitles: {
      on: inputs.subtitles ?? true,
      position: inputs.caption_position ?? "bottom",
      styleId: inputs.caption_style ?? "",
    },
  };
}

/**
 * Live's form: "Reuse example" (on fills the card's example: its clip into Spanish, French,
 * German and Italian; off clears the form; Replicate opens it on), the "Video" slot ("Pick the
 * video to translate"), and in the footer the languages, the lip sync and the subtitles, and the
 * round start, off until a clip and a language are there and while the dubbing model is not
 * offered. The run is the server's, so the panel says live's hand-off line once it started.
 */
export function LanguageChangerForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [video, setVideo] = useState<SlotVideo | null>(start?.video ?? null);
  const [languages, setLanguages] = useState<string[]>(start?.languages ?? FRESH.languages);
  const [lipSync, setLipSync] = useState<LipSyncValue>(start?.lipSync ?? FRESH.lipSync);
  const [subtitles, setSubtitles] = useState<SubtitlesValue>(start?.subtitles ?? FRESH.subtitles);
  // Live's "Reuse example": on fills the card's example, off clears the form.
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setVideo(values?.video ?? null);
    setLanguages(values?.languages ?? FRESH.languages);
    setLipSync(values?.lipSync ?? FRESH.lipSync);
    setSubtitles(values?.subtitles ?? FRESH.subtitles);
  };
  const catalog = useGetCatalog().data;
  const dubbing = catalog === undefined ? null : dubbingOf(catalog);
  const lipsync = catalog === undefined ? null : lipsyncOf(catalog);
  const missing = catalog !== undefined && dubbing === null;
  const styles = useListTemplateCaptionStyles(teamId).data;
  const styleId = subtitles.styleId || (styles?.default ?? "");
  // The dubbing model's source limits, ours (600 s, 200 MB; G7-video-dubbing 6, Language
  // Changer-2): a longer or bigger clip is said on the slot and never sent (the start refuses it
  // too).
  const problem = dubbing === null ? null : sourceProblem(dubbing.source, video);
  // Not before the catalog is read: the lip sync it may lack changes the chain and its quote.
  const inputs =
    catalog === undefined || video === null || languages.length === 0 || problem !== null
      ? null
      : {
          template: "language-changer" as const,
          video: videoSource(video),
          languages,
          // No lip-sync model: live's switch is off and cannot be turned on.
          lip_sync: lipSync.on && lipsync !== null,
          ...(lipSync.mode === null ? {} : { sync_mode: lipSync.mode }),
          subtitles: subtitles.on,
          caption_position: subtitles.position,
          ...(styleId === "" ? {} : { caption_style: styleId }),
        };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success && !missing ? quote.data : undefined,
    request: request.success && !missing ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">{TITLE}</Text>}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={4} wrap="nowrap">
            <LanguagesPicker options={dubbing?.target_languages ?? []} value={languages} onChange={setLanguages} />
            <LipSyncPicker
              caps={lipsync}
              value={lipsync === null ? { on: false, mode: null } : lipSync}
              onChange={setLipSync}
            />
            <Subtitles
              value={{ ...subtitles, styleId }}
              styles={styles?.styles ?? []}
              tip={SUBTITLES_TIP}
              disabled={false}
              onChange={setSubtitles}
            />
          </Group>
          <Group gap={8} wrap="nowrap">
            {/* Live's stored figure, one output a language, until PR 2's run quote (G14-template-costs). */}
            <CostChip estimate={props.template.estimate} outputs={languages.length} quote={job.lastPriced} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <VideoSlot
          teamId={teamId}
          brandId={brandId}
          label="Video"
          icon={<Film size={20} strokeWidth={1.5} aria-hidden />}
          subtitle="Pick the video to translate"
          tooltip={VIDEO_TIP}
          value={video}
          onChange={setVideo}
          problem={problem}
        />
        {missing ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {DUB_MODEL_MISSING}
          </Text>
        ) : null}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts), and its hand-off line. */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started. {HANDOFF}
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
